// File: app/src/components/workout/SessionEditDialog.js
//
// Mid-session session editor (#77, #175). Opened from the paused Coach, so
// correcting a phase that was prescribed wrong is a pause and a tap rather than
// abandoning the session and going back to the planner. The same editor serves
// a session on the phone and one running on the server (Khronos Timer, Go
// live): the caller decides what Apply does.
//
// The sibling of LoadAdjustDialog, and the split between them is deliberate.
// That one is a single figure on the phase in front of you, reachable in one
// tap without losing your place — the common correction. This is the whole
// remaining session, for the corrections that are not a number on the current
// set: a work interval prescribed at 60s that should always have been 90, a
// rep target that is wrong for every round, a name typed wrong, a phase that
// should not be there.
//
// It edits a DRAFT. Nothing reaches the running session until Apply, so an
// abandoned edit — or a mis-swipe on a row — leaves the session untouched. That
// matters more here than in the planner: the thing being edited is currently
// being performed. Steps already finished are locked: what was done is on the
// record, and editing it would change nothing that is still to come.

import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import usePlanEditor from "../../hooks/usePlanEditor";
import { finishedNodeIds } from "../../utils/liveTimeline";

const SessionEditDialog = ({
  open,
  onOpenChange,
  plan,
  // { path, loopCounters } of the phase in progress, for locking what's done.
  position = null,
  onSubmit,
}) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const { planDisplayProps } = usePlanEditor(setDraft);

  // Re-seed on every open so the draft starts from what the session is actually
  // running now — including any load adjustments made since the last edit.
  useEffect(() => {
    if (open) setDraft(plan || null);
  }, [open, plan]);

  // From the plan as it stood when the sheet opened: ids survive edits, so the
  // set still names the right rows as the draft changes.
  const lockedNodeIds = useMemo(
    () => (open && plan && position ? finishedNodeIds(plan, position) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, plan, position?.path?.join(), JSON.stringify(position?.loopCounters)],
  );

  const apply = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // A refusal (the server said no) keeps the sheet open with the draft.
      const ok = await onSubmit?.(draft);
      if (ok !== false) onOpenChange?.(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Edit session"
      description="Changes apply to the rest of this session. Finished steps stay as recorded."
      showDescription
      footer={
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-12 flex-1 text-base"
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="h-12 flex-1 text-base font-semibold"
            onClick={apply}
            disabled={!draft || saving}
          >
            Apply
          </Button>
        </div>
      }
    >
      {draft && (
        <SessionPlanDisplay
          plan={draft}
          editable
          showTotal={false}
          lockedNodeIds={lockedNodeIds}
          {...planDisplayProps}
        />
      )}
    </DetailSheet>
  );
};

export default SessionEditDialog;
